import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackCTAClick } from "../../utils/analytics";
import { getHerbIndex, segmentText } from "../../utils/herbLinker";

// Render a paragraph string with herb names auto-linked to their /herbs/:slug page.
const renderParagraph = (text, herbIndex, linkState) => {
  if (!herbIndex || !text || text.length < 40) return text;
  const segs = segmentText(text, herbIndex, linkState);
  if (segs.length === 1 && typeof segs[0] === "string") return text;
  return segs.map((s, i) =>
    typeof s === "string" ? (
      <React.Fragment key={i}>{s}</React.Fragment>
    ) : (
      <Link key={i} to={`/herbs/${s.slug}`} className="article-page__herblink">
        {s.label}
      </Link>
    )
  );
};

// Mid-article conversion box (soft trial CTA), injected partway through the body.
const MidArticleCTA = () => (
  <aside className="article-page__cta article-page__cta--mid">
    <p>
      Want a natural routine matched to your body and goals?{" "}
      <Link
        to="/start?source=article-mid"
        onClick={() => trackCTAClick("article_mid_start_trial", "article-mid")}
      >
        Get your personalized plan &rarr;
      </Link>
    </p>
  </aside>
);

// Shared renderer for the article block model, so admin-authored (Supabase) posts
// render with the EXACT same UI as the static JSON articles. Mirrors the switch in
// ArticleComponent.renderContent — same class names, same Article.css styling.
//   block types: header -> h2, paragraph -> p (first = lead), list -> ul, image -> img
//   sources[]  -> the "Sources" section
const ArticleBody = ({ content = [], sources = [] }) => {
  const blocks = Array.isArray(content) ? content : [];
  const list = Array.isArray(sources) ? sources : [];
  const firstParagraphIndex = blocks.findIndex(
    (b) => b && b.type === "paragraph"
  );
  // Inject the mid-article CTA after the 3rd block (only for longer articles).
  const midCtaAfter = blocks.length >= 5 ? 2 : -1;

  // Herb auto-linking: load the index once, then link the first occurrence of up
  // to 4 distinctive herb names across the article body.
  const [herbIndex, setHerbIndex] = useState(null);
  useEffect(() => {
    let active = true;
    getHerbIndex().then((idx) => active && setHerbIndex(idx));
    return () => {
      active = false;
    };
  }, []);
  const linkState = { used: new Set(), count: 0, cap: 4 };

  return (
    <>
      {blocks.map((item, index) => {
        if (!item || !item.type) return null;
        let el = null;
        switch (item.type) {
          case "header":
            el = <h2 className="article-page__h2">{item.text}</h2>;
            break;
          case "paragraph":
            el = (
              <p
                className={
                  index === firstParagraphIndex
                    ? "article-page__lead"
                    : "article-page__p"
                }
              >
                {renderParagraph(item.text, herbIndex, linkState)}
              </p>
            );
            break;
          case "image":
            el = item.src ? (
              <img
                src={item.src}
                alt={item.alt || ""}
                className="article-page__inline-img"
                loading="lazy"
              />
            ) : null;
            break;
          case "list":
            el = (
              <ul className="article-page__list">
                {(item.items || []).map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ul>
            );
            break;
          default:
            el = item.text ? <p className="article-page__p">{item.text}</p> : null;
        }
        return (
          <React.Fragment key={index}>
            {el}
            {index === midCtaAfter && <MidArticleCTA />}
          </React.Fragment>
        );
      })}

      {list.length > 0 && (
        <section className="article-page__sources">
          <h4 className="article-page__sources-title">Sources</h4>
          <ul>
            {list.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};

export default ArticleBody;
